import type { JSX } from 'react';
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { vkBotApi } from '../lib/vkBotApi';
import { hasPermission, type VkAppUser } from '../lib/authApi';

/**
 * Список подключённых групп + подключение новой по community-токену.
 */
export function GroupsPage({ user }: { user: VkAppUser }): JSX.Element {
  const qc = useQueryClient();
  const canManage = hasPermission(user, 'vk_bot:manage');
  const [connectOpen, setConnectOpen] = useState(false);
  const [token, setToken] = useState('');
  const [cityId, setCityId] = useState('');

  const groupsQuery = useQuery({
    queryKey: ['vk-bot', 'groups'],
    queryFn: () => vkBotApi.listGroups(),
  });

  const connectMut = useMutation({
    mutationFn: () =>
      vkBotApi.connect({ accessToken: token, cityId: cityId.trim() || null }),
    onSuccess: async (res) => {
      if (res.callbackOk) {
        void message.success(`Группа «${res.group.label}» подключена, события настроены`);
      } else {
        void message.warning(
          `Группа подключена, но callback-сервер не создан: ${res.callbackError ?? 'ошибка VK'}`,
        );
      }
      setConnectOpen(false);
      setToken('');
      setCityId('');
      await qc.invalidateQueries({ queryKey: ['vk-bot'] });
    },
    onError: (err) =>
      void message.error(err instanceof Error ? err.message : 'Не удалось подключить'),
  });

  const toggleMut = useMutation({
    mutationFn: (args: { id: string; isEnabled: boolean }) =>
      vkBotApi.updateGroup(args.id, { isEnabled: args.isEnabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vk-bot'] }),
  });

  const groups = groupsQuery.data ?? [];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Группы ВКонтакте
        </Typography.Title>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setConnectOpen(true)}>
            Подключить группу
          </Button>
        )}
      </Space>

      {groupsQuery.isError && (
        <Alert type="error" showIcon message="Не удалось загрузить группы" />
      )}

      {groups.length === 0 && !groupsQuery.isLoading ? (
        <Card>
          <Empty
            description={
              <span>
                Пока нет подключённых групп.
                {canManage && ' Нажмите «Подключить группу» — понадобится ключ доступа сообщества.'}
              </span>
            }
          />
        </Card>
      ) : (
        groups.map((g) => (
          <Card key={g.id} styles={{ body: { padding: 20 } }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <Space size={8}>
                  <Typography.Text strong style={{ fontSize: 16 }}>
                    {g.label}
                  </Typography.Text>
                  {!g.callbackOk && (
                    <Tag color="red">callback не настроен</Tag>
                  )}
                  {g.cityId && <Tag>{g.cityId}</Tag>}
                  <Tag color="blue">{g.settings.llmProvider}</Tag>
                </Space>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  vk.com/club{g.vkGroupId}
                  {g.lastEventAt &&
                    ` · последнее событие ${new Date(g.lastEventAt).toLocaleString('ru')}`}
                </div>
                {g.stats && (
                  <Space size={6} style={{ marginTop: 8 }}>
                    <Tag color="green">ответов за 7д: {g.stats.replied}</Tag>
                    <Tag>пропущено: {g.stats.skipped}</Tag>
                    {g.stats.failed > 0 && <Tag color="red">ошибок: {g.stats.failed}</Tag>}
                  </Space>
                )}
              </div>
              <Space size={12}>
                <Space size={6}>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    Бот
                  </Typography.Text>
                  <Switch
                    checked={g.isEnabled}
                    disabled={!canManage}
                    onChange={(v) => toggleMut.mutate({ id: g.id, isEnabled: v })}
                  />
                </Space>
                <Link to={`/groups/${g.id}`}>
                  <Button icon={<SettingOutlined />}>Настройки</Button>
                </Link>
              </Space>
            </div>
          </Card>
        ))
      )}

      <Modal
        open={connectOpen}
        onCancel={() => setConnectOpen(false)}
        title="Подключить группу ВК"
        okText="Подключить"
        confirmLoading={connectMut.isPending}
        onOk={() => connectMut.mutate()}
        okButtonProps={{ disabled: !token.trim() }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Нужен ключ доступа сообщества"
            description={
              <span style={{ fontSize: 12 }}>
                В группе ВК: Управление → Работа с API → Ключи доступа →
                «Создать ключ» с правами <b>«управление сообществом»</b> и{' '}
                <b>«стена»</b>. CRM сама настроит приём событий (второй
                callback-сервер, инбокс личных сообщений не затрагивается).
              </span>
            }
          />
          <Input.Password
            placeholder="vk1.a.…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="off"
          />
          <Input
            placeholder="Город (как в CRM, напр. «Юрга») — для промпта и геокодера"
            value={cityId}
            onChange={(e) => setCityId(e.target.value)}
          />
        </Space>
      </Modal>
    </Space>
  );
}
